/**
 * Task 245 — loads `graph-data/related.json` once per session.
 *
 * ~35 KB for 800 chapters, so it is cheap to hold in memory and the lookup is
 * synchronous afterwards. Deliberately NOT folded into `answer.final`: that
 * payload is cached verbatim by the exact-answer cache, and adding fields would
 * couple follow-ups to cache identity.
 *
 * `cache: "no-cache"` forces revalidation — the same trap use-corpus-data.ts
 * documents, where heuristic caching keeps a freshly published corpus invisible.
 *
 * Fails soft to `null` in every mode (offline, 404 on a deploy predating the
 * artifact, malformed payload). The chips simply do not render.
 */
import { useEffect, useState } from "react";
import type { RelatedFile } from "./related-passages";

export function useRelatedPassages(): RelatedFile | null {
  const [file, setFile] = useState<RelatedFile | null>(null);

  useEffect(() => {
    // The fetch may settle after unmount; suppress that stale state write.
    let alive = true;
    fetch("/graph-data/related.json", {
      headers: { Accept: "application/json" },
      cache: "no-cache",
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: RelatedFile) => {
        if (!alive) return;
        if (data === null || typeof data !== "object" || typeof data.related !== "object") return;
        setFile(data);
      })
      .catch(() => {
        /* fail soft — the answer is complete without follow-ups */
      });
    return () => {
      alive = false;
    };
  }, []);

  return file;
}
